import { ExternalArrowIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { FeaturePageCardDecoration } from "@/features/feature-page/feature-page-card-decoration";
import { featurePageCardStyle } from "@/features/feature-page/feature-page-card-style";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/types/rpc";
import { buildFeaturePagePath } from "@garimpa/lib/buildFeaturePagePath";
import { type ViewerLocale } from "@garimpa/lib/viewerPreferences";

export type FeaturePageCardItem = RouterOutputs["featurePages"]["getLanding"]["items"][number];

export function FeaturePageCard({
  item,
  locale,
  featured = false,
  familyLabel,
  headingLevel = "h2",
  imageSizes = "(min-width: 1280px) 60vw, 100vw",
  priority = featured,
  className,
}: {
  item: FeaturePageCardItem;
  locale: ViewerLocale;
  featured?: boolean;
  familyLabel?: string;
  headingLevel?: "h2" | "h3";
  imageSizes?: string;
  priority?: boolean;
  className?: string;
}) {
  const Heading = headingLevel;
  const image = item.image ? { ...item.image, sizes: imageSizes } : null;

  return (
    <Link
      className={cn(
        "group relative isolate block aspect-[4/3] min-w-0 overflow-hidden rounded-3xl border border-white/10 bg-zinc-950 shadow-lg transition duration-500 hover:-translate-y-1 hover:border-white/25 hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
        featured && "md:col-span-2 md:aspect-[2/1] xl:min-h-96",
        className,
      )}
      style={featurePageCardStyle(Boolean(image), item.accent)}
      to={buildFeaturePagePath(locale, item.family, item.slug)}
    >
      <FeaturePageCardDecoration
        item={item}
        image={image}
        familyLabel={familyLabel}
        priority={priority}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/35 to-black/5 transition duration-500 group-hover:via-black/25" />
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-end justify-between gap-5 p-5 text-white sm:p-7">
        <div className="min-w-0 max-w-3xl">
          <Heading
            className={cn(
              "line-clamp-2 text-2xl font-black leading-tight",
              featured && "md:text-4xl",
            )}
          >
            {item.title}
          </Heading>
          <p
            className={cn(
              "mt-2 line-clamp-2 max-w-2xl text-sm leading-relaxed text-white/75",
              featured && "md:text-base",
            )}
          >
            {item.description}
          </p>
        </div>
        <span className="grid size-11 shrink-0 place-items-center rounded-full border border-white/25 bg-black/20 backdrop-blur-md transition duration-300 group-hover:border-white/50 group-hover:bg-white group-hover:text-black">
          <ExternalArrowIcon className="text-xl" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}
